import { AiDrawer } from '@aigc/components';
import { useOverlayRef } from '@aigc/overlay-hook';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Spin, Tag } from 'antd';
import { lazy, Suspense, useState } from 'react';
import { getCheckComplianceOptions, type ComplianceItem } from './api';

const ComplianceDiffEditor = lazy(() =>
  import('./compliance-diff-editor').then((module) => ({
    default: module.ComplianceDiffEditor,
  })),
);
const riskTags = {
  low: { color: 'green', label: '低风险' },
  medium: { color: 'yellow', label: '中风险' },
  high: { color: 'red', label: '高风险' },
} satisfies Record<
  ComplianceItem['risk_level'],
  { color: string; label: string }
>;

export const ComplianceDrawer = (props: { prompt: string }) => {
  const overlayRef = useOverlayRef<string>();
  const [editedPrompt, setEditedPrompt] = useState<string>();
  const [editorReady, setEditorReady] = useState(false);
  const { data, isPending, isFetching, isError, error, refetch } = useQuery(
    getCheckComplianceOptions(props.prompt),
  );
  const modified = editedPrompt ?? data?.rewritten_content ?? '';
  const canSave = Boolean(
    data && !isFetching && editorReady && modified.trim(),
  );

  return (
    <AiDrawer
      title="脚本敏感内容分析"
      size={1600}
      onClose={() => overlayRef.close()}
      mask={{ closable: false }}
      keyboard={false}
      classNames={{ body: 'flex min-h-0 flex-col gap-4 lg:flex-row' }}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={() => overlayRef.close()}>取消</Button>
          <Button
            variant="solid"
            color="primary"
            disabled={!canSave}
            onClick={() => {
              if (canSave) overlayRef.close(modified);
            }}
          >
            保存修改
          </Button>
        </div>
      }
    >
      <section
        aria-label="敏感内容检测结果"
        className="min-h-0 w-[480px] shrink-0 overflow-auto"
      >
        <Alert
          type="info"
          className="mb-4"
          title={
            <>
              各平台审核标准不同，
              <span className="font-bold">检测结果仅供参考</span>
              ，请结合原因说明和修改建议确认改写内容。
            </>
          }
        />
        {isPending ? (
          <div role="status" className="flex justify-center py-12">
            <span className="flex items-center gap-2">
              <Spin />
              敏感词检测中，请稍候...
            </span>
          </div>
        ) : isError ? (
          <Alert
            classNames={{ root: 'mt-4' }}
            type="error"
            showIcon
            title="检测失败"
            description={error.message || '请稍后重试'}
            action={
              <Button onClick={() => void refetch()} loading={isFetching}>
                重试
              </Button>
            }
          />
        ) : data.sensitive_words.length === 0 ? (
          <Empty description="未检测到敏感内容，已保留原文" />
        ) : (
          <ol className="m-0 mt-4 list-none space-y-3 p-0">
            {data.sensitive_words.map((item, index) => (
              <li
                key={index}
                className="rounded-lg border border-solid border-gray-200 p-4"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h3 className="m-0 text-sm font-medium">
                    敏感内容 {index + 1}
                  </h3>
                  <Tag color={riskTags[item.risk_level].color}>
                    {riskTags[item.risk_level].label}
                  </Tag>
                </div>
                <p className="whitespace-pre-wrap break-words rounded bg-gray-50 p-2">
                  {item.sensitive_content}
                </p>
                <dl className="mb-0 mt-2 space-y-2 text-sm">
                  <dt className="font-medium">原因说明</dt>
                  <dd className="m-0 whitespace-pre-wrap break-words text-gray-600">
                    {item.reason}
                  </dd>
                  <dt className="font-medium">修改建议</dt>
                  <dd className="m-0 whitespace-pre-wrap break-words text-gray-600">
                    {item.suggestion}
                  </dd>
                </dl>
              </li>
            ))}
          </ol>
        )}
      </section>
      <section
        aria-label="改写前后内容对比"
        className="flex min-h-[400px] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-solid border-gray-200 lg:min-h-0"
      >
        <div className="border-b border-solid border-gray-200 px-4 py-3">
          <h2 className="m-0 text-base font-medium">改写建议</h2>
          <p className="mb-0 mt-1 text-sm text-gray-500">
            可以继续对改写后的内容进行编辑，确认后保存修改。
          </p>
        </div>
        <div className="min-h-0 flex-1">
          {data ? (
            <Suspense
              fallback={
                <div role="status" className="flex justify-center py-12">
                  <span className="flex items-center gap-2">
                    <Spin />
                    正在加载对比编辑器...
                  </span>
                </div>
              }
            >
              <ComplianceDiffEditor
                original={props.prompt}
                modified={modified}
                onChange={setEditedPrompt}
                onReady={() => setEditorReady(true)}
              />
            </Suspense>
          ) : (
            <div className="flex h-full items-center justify-center p-8">
              <Empty
                description={
                  isPending
                    ? '检测完成后将在这里看到改写对比'
                    : '检测成功后可查看改写对比'
                }
              />
            </div>
          )}
        </div>
      </section>
    </AiDrawer>
  );
};
